import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
	faArrowUpRightFromSquare,
	faCircleMinus,
	faPlus,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ScoringOption } from "./matrixModel";

interface ScoringPickerProps {
	anchorEl: HTMLElement;
	options: ScoringOption[];
	selectedId: number | null;
	/** True when the persona exists in this segment; offers "deactivate" instead of nothing. */
	linked: boolean;
	/** Investor rows switch the persona for every investor type at once. */
	isInvestorRow: boolean;
	onSelect: (criteriaId: number) => void;
	onClear: () => void;
	onEdit: (criteriaId: number) => void;
	onCreate: () => void;
	onDeactivate: () => void;
	onClose: () => void;
	portalContainer?: HTMLElement | null;
}

const WIDTH = 330;

/**
 * The menu under a matrix cell (PREC-4055): pick a lead scoring, open it, create
 * a new one for this cell, clear the cell, or switch the persona off here.
 */
export const ScoringPicker: FunctionComponent<ScoringPickerProps> = ({
	anchorEl,
	options,
	selectedId,
	linked,
	isInvestorRow,
	onSelect,
	onClear,
	onEdit,
	onCreate,
	onDeactivate,
	onClose,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const menuRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	useLayoutEffect(() => {
		const update = () => {
			const rect = anchorEl.getBoundingClientRect();
			const left = Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8));
			const height = menuRef.current?.offsetHeight ?? 0;
			const top =
				rect.bottom + 4 + height > window.innerHeight - 8
					? Math.max(8, rect.top - height - 4)
					: rect.bottom + 4;

			setPosition({ top, left });
		};

		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [anchorEl]);

	useEffect(() => {
		const onMouseDown = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (menuRef.current?.contains(target) || anchorEl.contains(target)) {
				return;
			}

			onClose();
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		};

		document.addEventListener("mousedown", onMouseDown);
		document.addEventListener("keydown", onKeyDown);

		return () => {
			document.removeEventListener("mousedown", onMouseDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [anchorEl, onClose]);

	const row =
		"flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text hover:bg-background-light";

	return createPortal(
		<div
			ref={menuRef}
			className="fixed z-[1600] rounded-lg border border-border-light bg-white p-1 shadow-lg"
			data-testid="scoring-picker"
			role="menu"
			style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width: WIDTH }}
		>
			<div className="px-2 pb-1 pt-1.5 text-[11px] font-semibold text-text-disabled">
				{t("leadScoring.matrix.pickTitle")}
			</div>
			{options.length === 0 && (
				<div className="px-2 py-1.5 text-xs text-text-disabled">
					{t("leadScoring.noLeadScoringsConfigured")}
				</div>
			)}
			{options.map((option) => (
				<div
					key={option.id}
					className={classNames(
						"group",
						row,
						option.id === selectedId ? "bg-blue-action-200 font-medium text-blue-action" : ""
					)}
					role="menuitem"
					onClick={() => onSelect(option.id)}
				>
					<span className={classNames("h-2 w-2 shrink-0 rounded-full", option.swatch)} />
					<span className="min-w-0 flex-1 truncate">{option.name}</span>
					{!option.locked && (
						<span className="shrink-0 text-[10px] text-text-disabled">
							{t("leadScoring.inactive")}
						</span>
					)}
					<button
						className="hidden shrink-0 cursor-pointer items-center gap-1 text-[11px] font-medium text-blue-action group-hover:flex"
						type="button"
						onClick={(event) => {
							event.stopPropagation();
							onEdit(option.id);
						}}
					>
						<Icon className="h-3 w-3" icon={faArrowUpRightFromSquare} />
						{t("leadScoring.matrix.editScoring")}
					</button>
				</div>
			))}
			<div className="my-1 h-px bg-border-light" />
			<button
				className={classNames(row, "font-medium text-blue-action")}
				type="button"
				onClick={onCreate}
			>
				<Icon className="h-3 w-3" icon={faPlus} />
				{t("leadScoring.matrix.createScoring")}
			</button>
			{selectedId !== null && (
				<button className={row} type="button" onClick={onClear}>
					<Icon className="h-3 w-3 text-text-disabled" icon={faXmark} />
					{t("leadScoring.matrix.clearScoring")}
				</button>
			)}
			{linked && (
				<button className={row} type="button" onClick={onDeactivate}>
					<Icon className="h-3 w-3 text-text-disabled" icon={faCircleMinus} />
					{t(
						isInvestorRow
							? "leadScoring.matrix.deactivateForInvestors"
							: "leadScoring.matrix.deactivateHere"
					)}
				</button>
			)}
		</div>,
		portalContainer ?? document.body
	);
};
